import React, {useCallback} from 'react';
import {SearchOnTypeSelectAutocomplete, SelectAutocomplete, Text} from 'components-lib';
import {FieldProps} from 'formik';
import {IOption, IWithDisabled} from 'models';
import {Field} from './Field';

interface IFormSelectAutocompleteProps extends IWithDisabled {
  name: string;
  open?: boolean;
  options: IOption[];
  multiple?: boolean;
  noOptionsText?: string;
  withCheckboxRenderOption?: boolean;
  withFontWeightBold?: boolean;
  disableCloseOnSelect?: boolean;
  /** Allow the same option value more than once in a multi-select (chip per pick). */
  allowDuplicateSelections?: boolean;
  loading?: boolean;
  withSearchOnType?: boolean;
  isFilter?: boolean;
  groupBy?: (option: IOption) => string;
  filterOptions?: (options: IOption[]) => IOption[];
  inputChangeHandler?: (value: string) => void;
  onOpen?: () => void;
}

export function FormSelectAutocomplete({
  name,
  open,
  options,
  multiple = false,
  noOptionsText = 'No options',
  disabled = false,
  withCheckboxRenderOption = true,
  withFontWeightBold = false,
  withSearchOnType,
  loading = false,
  isFilter,
  groupBy,
  inputChangeHandler,
  disableCloseOnSelect,
  allowDuplicateSelections = false,
  filterOptions,
  onOpen,
}: IFormSelectAutocompleteProps) {
  return (
    <Field
      loading={loading}
      name={name}
      open={open}
      options={options}
      multiple={multiple}
      noOptionsText={noOptionsText}
      disabled={disabled}
      isFilter={isFilter}
      withCheckboxRenderOption={withCheckboxRenderOption}
      withFontWeightBold={withFontWeightBold}
      inputChangeHandler={inputChangeHandler}
      withSearchOnType={withSearchOnType}
      disableCloseOnSelect={disableCloseOnSelect}
      allowDuplicateSelections={allowDuplicateSelections}
      onOpen={onOpen}
      groupBy={groupBy}
      filterOptions={filterOptions}
      // Decorated component renders the single Text.Error — avoid Field's duplicate.
      suppressFieldError
      Component={SelectAutocompleteDecorated}
    />
  );
}

interface ISelectDecoratedProps extends FieldProps, IFormSelectAutocompleteProps {
  onBlur: () => void;
}

function SelectAutocompleteDecorated({
  field: {name},
  form: {setFieldValue, values, errors},
  onBlur,
  options = [],
  multiple = false,
  noOptionsText,
  disabled = false,
  withCheckboxRenderOption = true,
  withFontWeightBold = false,
  withSearchOnType,
  loading = false,
  isFilter,
  groupBy,
  inputChangeHandler,
  disableCloseOnSelect,
  allowDuplicateSelections = false,
  filterOptions,
}: ISelectDecoratedProps) {
  const value = values[name];
  const isValueArray = Array.isArray(value);
  const fieldError = errors[name];
  const errorMessage = typeof fieldError === 'string' ? fieldError : undefined;
  const showError = !!errorMessage;

  const changeHandler = useCallback(
    (newValue: IOption | IOption[] | null, reason?: string) => {
      if (reason === 'reset' || reason === 'blur') {
        return;
      }

      const toStoredValue = (entry: IOption | number | string) => {
        const raw = entry !== null && typeof entry === 'object' && 'value' in entry ? (entry as IOption).value : entry;
        if (raw === null || raw === undefined || raw === '') {
          return raw;
        }
        // Persist numeric option ids as numbers so upload/update payloads keep real KSA/competency ids.
        const asNumber = Number(raw);
        return !Number.isNaN(asNumber) && String(raw).trim() !== '' ? asNumber : raw;
      };

      if (Array.isArray(newValue)) {
        setFieldValue(
          name,
          newValue.map((v) => toStoredValue(v as IOption | number | string))
        );
        return;
      }

      setFieldValue(name, newValue ? toStoredValue(newValue) : newValue);
    },
    [name, setFieldValue]
  );

  const extractEntryId = (entry: unknown): number | string | null => {
    if (entry === null || entry === undefined || entry === '') {
      return null;
    }
    if (typeof entry === 'object') {
      if ('value' in (entry as Record<string, unknown>)) {
        return (entry as IOption).value;
      }
      if ('id' in (entry as Record<string, unknown>)) {
        return (entry as {id: number | string}).id;
      }
      return null;
    }
    return entry as number | string;
  };

  const extractEntryLabel = (entry: unknown, id: number | string): string => {
    if (entry !== null && typeof entry === 'object') {
      const obj = entry as {label?: string; name?: string; displayText?: string};
      return obj.label || obj.name || obj.displayText || String(id);
    }
    return String(id);
  };

  // Preserve value order; include stubs when selected ids are not in the current options list
  // (common for async search selects after hydrate / after a new search replaces options).
  const selected = isValueArray
    ? (value as unknown[])
        .map((entry, index) => {
          const id = extractEntryId(entry);
          if (id === null) {
            return null;
          }
          const option =
            options.find(
              (o) =>
                (o.value === id || String(o.value) === String(id)) &&
                // Prefer a real display label over an id-only stub when both exist.
                o.label &&
                o.label !== String(id)
            ) ?? options.find((o) => o.value === id || String(o.value) === String(id));
          const resolved = option ?? {value: id, label: extractEntryLabel(entry, id)};
          return allowDuplicateSelections ? {...resolved, selectionIndex: index} : resolved;
        })
        .filter((option): option is IOption => !!option)
    : [];

  const selectedForControl =
    allowDuplicateSelections || withSearchOnType
      ? selected
      : // Prefer mapped selection (includes hydrated {id,name} labels / stubs) over options-list filter.
      selected.length
      ? selected
      : options.filter((option) =>
          (value as unknown[]).some((entry) => {
            const id = extractEntryId(entry);
            return id !== null && (option.value === id || String(option.value) === String(id));
          })
        );

  // Ensure Autocomplete can resolve selected chips that are not in the latest search results.
  const optionsWithSelected = withSearchOnType
    ? [
        ...selected.filter(
          (selectedOption) => !options.some((option) => String(option.value) === String(selectedOption.value))
        ),
        ...options,
      ]
    : options;

  const control = withSearchOnType ? (
    <SearchOnTypeSelectAutocomplete
      label={name}
      options={optionsWithSelected}
      multiple={multiple}
      noOptionsText={noOptionsText}
      disabled={disabled}
      withCheckboxRenderOption={withCheckboxRenderOption}
      loading={loading}
      isFilter={isFilter}
      inputChangeHandler={inputChangeHandler}
      disableCloseOnSelect={disableCloseOnSelect}
      filterOptions={filterOptions}
      values={selectedForControl}
      changeHandler={changeHandler}
      blurHandler={onBlur}
      error={showError}
    />
  ) : (
    <SelectAutocomplete
      options={options}
      multiple={multiple}
      noOptionsText={noOptionsText}
      disabled={disabled}
      withCheckboxRenderOption={withCheckboxRenderOption}
      withFontWeightBold={withFontWeightBold}
      loading={loading}
      groupBy={groupBy}
      inputChangeHandler={inputChangeHandler}
      disableCloseOnSelect={disableCloseOnSelect}
      allowDuplicateSelections={allowDuplicateSelections}
      filterOptions={filterOptions}
      values={selectedForControl}
      changeHandler={changeHandler}
      blurHandler={onBlur}
      error={showError}
    />
  );

  return (
    <>
      {control}
      {errorMessage ? <Text.Error>{errorMessage}</Text.Error> : null}
    </>
  );
}
